import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { getToken, setToken, clearToken, authHeaders } from "@/lib/auth";
import { track } from "@/lib/analytics";

const API = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext(null);

// FastAPI 422 returns detail as an array of {msg} objects; coerce to a string.
function apiError(detail, fallback) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg).filter(Boolean).join(" ") || fallback;
  return fallback;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined=checking, null=guest, obj=authed
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!getToken()) { setUser(null); setLoading(false); return null; }
    try {
      // One quiet retry so a single dropped request never looks like a sign-out.
      const me = () => fetch(`${API}/api/auth/me`, { headers: { ...authHeaders() } });
      const res = await me().catch(() => new Promise((r) => setTimeout(r, 800)).then(me));
      if (res.status === 401 || res.status === 403) { clearToken(); setUser(null); return null; }
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = await res.json();
      setUser(data.user);
      return data.user;
    } catch {
      // Network blips / aborted requests / server errors never sign the person out.
      let kept;
      setUser((u) => { kept = u === undefined ? null : u; return kept; });
      return kept ?? null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const register = async ({ email, password, name, anonId, marketingOptIn = false }) => {
    const res = await fetch(`${API}/api/auth/register`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, name, anon_id: anonId || undefined, marketing_opt_in: marketingOptIn === true }),
    });
    const data = await res.json();
    if (!res.ok) {
      const err = new Error(apiError(data.detail, "Could not create account."));
      err.status = res.status;
      throw err;
    }
    setToken(data.token); setUser(data.user);
    // GA4 sign_up — only after a legitimate account is created (no PII sent).
    track("sign_up", { method: "email" });
    return data.user;
  };

  const login = async ({ email, password }) => {
    const res = await fetch(`${API}/api/auth/login`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) {
      const err = new Error(apiError(data.detail, "Sign in failed."));
      err.status = res.status;
      throw err;
    }
    setToken(data.token); setUser(data.user); return data.user;
  };

  // Always resolves with the same generic message (no account enumeration).
  const forgotPassword = async (email) => {
    try {
      const res = await fetch(`${API}/api/auth/forgot-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json().catch(() => ({}));
      return data.message || "If an account exists for that email, a password reset link is on its way.";
    } catch {
      return "If an account exists for that email, a password reset link is on its way.";
    }
  };

  const resetPassword = async (token, password) => {
    const res = await fetch(`${API}/api/auth/reset-password`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, password }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(apiError(data.detail, "Could not reset your password."));
    return true;
  };

  const logout = () => { clearToken(); setUser(null); };

  return (
    <AuthContext.Provider value={{ user, loading, register, login, logout, refresh, forgotPassword, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
